import { useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

// @mui
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";

// icons
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

// ZionLadder UI
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

/**
 * Question
 * A small FAQ item component.
 *
 * Props (main):
 * - question: string | node
 * - answer: string | node
 * - expanded: boolean (controls whether answer is visible)
 *
 * Optional:
 * - onToggle: function (called when user clicks header)
 * - divider: boolean (show a subtle bottom border)
 */
function Question({ question, answer, expanded, onToggle, divider }) {
  const canToggle = typeof onToggle === "function";

  const answerRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    // Prefer copying plain text. If answer is a React node, fall back to rendered text.
    const text =
      typeof answer === "string"
        ? answer
        : (answerRef.current && answerRef.current.innerText) || "";

    if (!text) return;

    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.style.top = "-9999px";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }

      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (e) {
      // ignore
    }
  };

  const headerProps = useMemo(
    () =>
      canToggle
        ? {
            role: "button",
            tabIndex: 0,
            onClick: () => onToggle(!expanded),
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") onToggle(!expanded);
            },
          }
        : {},
    [canToggle, expanded, onToggle]
  );

  return (
    <MKBox
      sx={({ palette }) => ({
        borderRadius: "lg",
        border: `1px solid ${palette.grey[200]}`,
        overflow: "hidden",
      })}
    >
      <MKBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        px={2}
        py={1.5}
        sx={({ palette }) => ({
          background: palette.common.white,
          cursor: canToggle ? "pointer" : "default",
          ...(divider
            ? {
                borderBottom: `1px solid ${palette.grey[200]}`,
              }
            : {}),
        })}
        {...headerProps}
      >
        <MKTypography variant="button" fontWeight="bold" color="dark">
          {question}
        </MKTypography>

        <IconButton
          size="small"
          disabled={!canToggle}
          aria-label={expanded ? "collapse" : "expand"}
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 150ms ease",
          }}
          onClick={(e) => {
            // If header is clickable, we still stop here to avoid double-toggle.
            e.stopPropagation();
            if (canToggle) onToggle(!expanded);
          }}
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </MKBox>

      <Collapse in={!!expanded} timeout="auto" unmountOnExit>
        <MKBox px={2} py={1.75}>
          <MKBox display="flex" alignItems="flex-start" justifyContent="space-between" mb={0.75}>
            <MKTypography
              variant="caption"
              color={copied ? "success" : "text"}
              fontWeight={copied ? "bold" : "regular"}
            >
              {copied ? "Copied" : ""}
            </MKTypography>

            <IconButton
              size="small"
              aria-label="copy"
              onClick={handleCopy}
              sx={({ palette }) => ({
                border: `1px solid ${palette.grey[200]}`,
                borderRadius: "lg",
              })}
            >
              {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            </IconButton>
          </MKBox>

          <MKBox ref={answerRef}>
            <MKTypography variant="body2" color="text">
              {answer}
            </MKTypography>
          </MKBox>
        </MKBox>
      </Collapse>
    </MKBox>
  );
}

Question.propTypes = {
  question: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  answer: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
  divider: PropTypes.bool,
};

Question.defaultProps = {
  expanded: false,
  onToggle: null,
  divider: true,
};

export default Question;
